import React, { useMemo, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { durations } from '@atlaskit/motion';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import Tooltip from '@atlaskit/tooltip';

import { expValEquals } from '@confluence/feature-experiments';

const i18n = defineMessages({
	expand: {
		id: 'share-and-restrict-dialog.useExpandIconRenderer.expand',
		defaultMessage: 'View specific access',
		description: 'Tooltip text when expand icon is collapsed',
	},
	collapse: {
		id: 'share-and-restrict-dialog.useExpandIconRenderer.collapse',
		defaultMessage: 'Collapse',
		description: 'Tooltip text when expand icon is expanded',
	},
});

const chevronStyles = xcss({
	transform: 'rotate(0deg)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	transition: `all ${durations.small}ms linear`,
});

const expandedStyles = xcss({
	transform: 'rotate(90deg)',
});

const psaExpandBackgroundStyle = xcss({
	width: token('space.400'),
	height: token('space.400'),
	borderRadius: 'radius.small',
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	background: 'none',
});

export type UseExpandIconRendererConfig = {
	/**
	 * Whether the row/content is currently expanded
	 */
	isExpanded: boolean;

	/**
	 * Whether the specific access dialog is open
	 * When true: shows fallback component instead of icon
	 * When false: shows icon
	 */
	isSpecificAccessDialogOpen?: boolean;

	/**
	 * Whether the row is currently hovered
	 * Used to show icon only on hover in control mode
	 */
	rowHovered?: boolean;

	/**
	 * Optional custom expand message override
	 */
	expandMessage?: string;

	/**
	 * Component to render when icon should NOT be shown
	 * In SpecificAccessRow: PrincipalAccess component
	 * In FacepileRow: empty div
	 */
	fallbackComponent?: ReactNode;
};

export type UseExpandIconRendererReturn = {
	/**
	 * Render function that returns either the icon or fallback component
	 * Handles all JSX logic and conditional rendering
	 */
	renderIcon: () => ReactNode;
};

/**
 * useExpandIconRenderer
 *
 * A custom hook that encapsulates the complete icon rendering logic for expand/collapse icons
 * in the Share and Restrict Dialog.
 */
export const useExpandIconRenderer = (
	config: UseExpandIconRendererConfig,
): UseExpandIconRendererReturn => {
	const {
		isExpanded,
		isSpecificAccessDialogOpen = false,
		rowHovered = false,
		expandMessage: customMessage,
		fallbackComponent,
	} = config;
	const { formatMessage } = useIntl();

	const expandMessage = useMemo(() => {
		if (customMessage) {
			return customMessage;
		}
		return formatMessage(isExpanded ? i18n.collapse : i18n.expand);
	}, [isExpanded, customMessage, formatMessage]);

	const renderIcon = useMemo((): (() => ReactNode) => {
		return () => {
			if (isSpecificAccessDialogOpen) {
				return fallbackComponent || <div />;
			}

			if (rowHovered || expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
				return (
					<Tooltip content={expandMessage}>
						{(tooltipProps) => (
							<Box
								as="span"
								{...tooltipProps}
								aria-expanded={isExpanded}
								xcss={psaExpandBackgroundStyle}
							>
								<Inline alignBlock="center" alignInline="center">
									<Box as="span" xcss={[chevronStyles, isExpanded && expandedStyles]}>
										<ChevronRightIcon label={expandMessage} size="small" />
									</Box>
								</Inline>
							</Box>
						)}
					</Tooltip>
				);
			}

			return fallbackComponent || <div />;
		};
	}, [isSpecificAccessDialogOpen, rowHovered, expandMessage, isExpanded, fallbackComponent]);

	return {
		renderIcon,
	};
};
